import { AppLayout } from "@/layouts/AppLayout"
import { formatDate, distanceOfTimeInWordsToNow } from "@/utils/helpers"

interface Issue {
  severity: "warning" | "info"
  source: string
  message: string
  detail: string
}

interface SourceStat {
  id: string
  name: string
  active: boolean
  total: number
  recent: number
  avgPins: number
  maxPins: number
  noImage: number
  latestPublished: string | null
  latestDiscovered: string | null
}

interface FeedRecipe {
  id: string
  title: string
  url: string
  score: number
  pinterestCount: number
  publishedAt: string | null
  sourceName: string
}

interface RecentRecipe {
  id: string
  title: string
  url: string
  discoveredAt: string | null
  publishedAt: string | null
  pinterestCount: number
  sourceName: string
  hasImage: boolean
}

interface ScheduledJob {
  name: string
  cron: string
  className: string
}

interface Stats {
  totalRecipes: number
  recipesRecent7d: number
  activeSources: number
  totalSources: number
  cacheExists: boolean
  recipesWithDates: number
  recipesNoImage: number
  recipesNoPins: number
  recipesHidden: number
}

interface SystemInfo {
  rubyVersion: string
  railsVersion: string
  environment: string
  database: string
  cacheStore: string
  timeZone: string
}

interface Props {
  stats: Stats
  sources: SourceStat[]
  feedPreview: FeedRecipe[]
  recentRecipes: RecentRecipe[]
  scheduledJobs: ScheduledJob[]
  potentialIssues: Issue[]
  systemInfo: SystemInfo
  generatedAt: string
}

export default function AdminDashboard({
  stats,
  sources,
  feedPreview,
  recentRecipes,
  scheduledJobs,
  potentialIssues,
  systemInfo,
  generatedAt,
}: Props) {
  return (
    <AppLayout title="Admin Dashboard">
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-7xl space-y-8">
          <header className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-slate-500">System health and debugging information</p>
            </div>
            <div className="text-right text-sm text-slate-500">
              <p>Generated: {generatedAt}</p>
              <p>Rails env: {systemInfo.environment}</p>
            </div>
          </header>

          {potentialIssues.length > 0 && (
            <IssuesSection issues={potentialIssues} />
          )}

          <StatsSection stats={stats} />
          <SourcesSection sources={sources} />
          <FeedPreviewSection recipes={feedPreview} />
          <RecentlyDiscoveredSection recipes={recentRecipes} />
          <ScheduledJobsSection jobs={scheduledJobs} />
          <ScoringFormulaSection />
          <SystemInfoSection info={systemInfo} />
        </div>
      </div>
    </AppLayout>
  )
}

function IssuesSection({ issues }: { issues: Issue[] }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">
        ⚠️ Potential Issues ({issues.length})
      </h2>
      <div className="space-y-2">
        {issues.map((issue, i) => (
          <div
            key={i}
            className={`flex items-start gap-3 rounded-lg p-3 ${
              issue.severity === "warning" ? "bg-amber-50" : "bg-slate-50"
            }`}
          >
            <span className="text-lg">
              {issue.severity === "warning" ? "🟡" : "🔵"}
            </span>
            <div className="flex-1">
              <p className="font-medium text-slate-900">
                <span className="text-slate-500">{issue.source}:</span> {issue.message}
              </p>
              <p className="text-sm text-slate-500">{issue.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function StatsSection({ stats }: { stats: Stats }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">📊 Overview</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard value={stats.totalRecipes} label="Total Recipes" />
        <StatCard value={stats.recipesRecent7d} label="Recent (7 days)" />
        <StatCard
          value={`${stats.activeSources}/${stats.totalSources}`}
          label="Active Sources"
        />
        <StatCard
          value={stats.cacheExists ? "✓ Cached" : "○ Empty"}
          label="Feed Cache"
          highlight={stats.cacheExists}
        />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard value={stats.recipesWithDates} label="With Published Date" />
        <StatCard
          value={stats.recipesNoImage}
          label="Missing Images"
          warning={stats.recipesNoImage > 0}
        />
        <StatCard value={stats.recipesNoPins} label="Zero Pinterest Saves" />
        <StatCard value={stats.recipesHidden} label="Hidden" />
      </div>
    </section>
  )
}

function StatCard({
  value,
  label,
  highlight = false,
  warning = false,
}: {
  value: string | number
  label: string
  highlight?: boolean
  warning?: boolean
}) {
  const textClass = highlight
    ? "text-green-600"
    : warning
      ? "text-amber-600"
      : "text-slate-900"

  return (
    <div className="rounded-lg bg-slate-50 p-4">
      <p className={`text-2xl font-bold ${textClass}`}>{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  )
}

function SourcesSection({ sources }: { sources: SourceStat[] }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">🌐 Sources</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="pb-3 pr-4">Source</th>
              <th className="pb-3 pr-4 text-right">Total</th>
              <th className="pb-3 pr-4 text-right">Recent</th>
              <th className="pb-3 pr-4 text-right">Avg Pins</th>
              <th className="pb-3 pr-4 text-right">Max Pins</th>
              <th className="pb-3 pr-4 text-right">No Image</th>
              <th className="pb-3 pr-4">Latest Published</th>
              <th className="pb-3 pr-4">Latest Discovered</th>
              <th className="pb-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sources.map((s) => (
              <tr key={s.id} className={s.active ? "" : "opacity-50"}>
                <td className="py-3 pr-4 font-medium text-slate-900">{s.name}</td>
                <td className="py-3 pr-4 text-right text-slate-600">{s.total}</td>
                <td
                  className={`py-3 pr-4 text-right ${
                    s.recent === 0 && s.active
                      ? "font-medium text-amber-600"
                      : "text-slate-600"
                  }`}
                >
                  {s.recent}
                </td>
                <td className="py-3 pr-4 text-right text-slate-600">{s.avgPins}</td>
                <td className="py-3 pr-4 text-right text-slate-600">{s.maxPins}</td>
                <td
                  className={`py-3 pr-4 text-right ${
                    s.noImage > 0 ? "text-amber-600" : "text-slate-600"
                  }`}
                >
                  {s.noImage}
                </td>
                <td className="py-3 pr-4 text-slate-500">{formatDate(s.latestPublished)}</td>
                <td className="py-3 pr-4 text-slate-500">{formatDate(s.latestDiscovered)}</td>
                <td className="py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      s.active
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {s.active ? "Active" : "Disabled"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function FeedPreviewSection({ recipes }: { recipes: FeedRecipe[] }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">
        📰 Current Feed (Top 10)
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="pb-3 pr-4 text-right">#</th>
              <th className="pb-3 pr-4 text-right">Score</th>
              <th className="pb-3 pr-4 text-right">Pins</th>
              <th className="pb-3 pr-4">Age</th>
              <th className="pb-3 pr-4">Source</th>
              <th className="pb-3">Title</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {recipes.map((recipe, i) => (
              <tr key={recipe.id}>
                <td className="py-3 pr-4 text-right text-slate-400">{i + 1}</td>
                <td className="py-3 pr-4 text-right font-mono font-medium text-slate-900">
                  {recipe.score}
                </td>
                <td className="py-3 pr-4 text-right text-slate-600">
                  {recipe.pinterestCount}
                </td>
                <td className="py-3 pr-4 text-slate-500">
                  {distanceOfTimeInWordsToNow(recipe.publishedAt)}
                </td>
                <td className="py-3 pr-4 text-slate-600">{recipe.sourceName}</td>
                <td className="py-3 font-medium text-slate-900">
                  <a
                    href={recipe.url}
                    target="_blank"
                    rel="noopener"
                    className="hover:text-blue-600 hover:underline"
                  >
                    {recipe.title.length > 50
                      ? recipe.title.slice(0, 50) + "..."
                      : recipe.title}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function RecentlyDiscoveredSection({ recipes }: { recipes: RecentRecipe[] }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">
        🆕 Recently Discovered (Last 10)
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
            <tr>
              <th className="pb-3 pr-4">Discovered</th>
              <th className="pb-3 pr-4">Published</th>
              <th className="pb-3 pr-4">Source</th>
              <th className="pb-3 pr-4">Title</th>
              <th className="pb-3 pr-4 text-right">Pins</th>
              <th className="pb-3">Image</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {recipes.map((recipe) => (
              <tr key={recipe.id}>
                <td className="py-3 pr-4 text-slate-500">
                  {formatDate(recipe.discoveredAt)}
                </td>
                <td className="py-3 pr-4 text-slate-500">
                  {formatDate(recipe.publishedAt)}
                </td>
                <td className="py-3 pr-4 text-slate-600">{recipe.sourceName}</td>
                <td className="py-3 pr-4 font-medium text-slate-900">
                  <a
                    href={recipe.url}
                    target="_blank"
                    rel="noopener"
                    className="hover:text-blue-600 hover:underline"
                  >
                    {recipe.title.length > 40
                      ? recipe.title.slice(0, 40) + "..."
                      : recipe.title}
                  </a>
                </td>
                <td className="py-3 pr-4 text-right text-slate-600">
                  {recipe.pinterestCount}
                </td>
                <td className="py-3">
                  {recipe.hasImage ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="text-amber-600">✗</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ScheduledJobsSection({ jobs }: { jobs: ScheduledJob[] }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">⏰ Scheduled Jobs</h2>
      {jobs.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <th className="pb-3 pr-4">Job Name</th>
                <th className="pb-3 pr-4">Schedule</th>
                <th className="pb-3">Class</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => (
                <tr key={job.name}>
                  <td className="py-3 pr-4 font-medium text-slate-900">{job.name}</td>
                  <td className="py-3 pr-4 font-mono text-slate-600">{job.cron}</td>
                  <td className="py-3 text-slate-600">{job.className}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-slate-500">No scheduled jobs configured</p>
      )}
    </section>
  )
}

function ScoringFormulaSection() {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">📐 Scoring Formula</h2>
      <div className="rounded-lg bg-slate-50 p-4 font-mono text-sm">
        <p className="text-slate-700">Score = Popularity + Recency</p>
        <p className="mt-2 text-slate-500">
          Popularity = min(√pins × 4, 80){" "}
          <span className="text-slate-400">// 0-80 points</span>
        </p>
        <p className="text-slate-500">
          Recency = max(20 × (1 - age_hours/168), 0){" "}
          <span className="text-slate-400">// 0-20 points, decays over 7 days</span>
        </p>
      </div>
      <div className="mt-4">
        <h3 className="mb-2 font-medium text-slate-900">Example Scores</h3>
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <div className="rounded bg-slate-50 p-2">
            <p className="font-mono text-slate-900">400 pins, 0d = 100</p>
          </div>
          <div className="rounded bg-slate-50 p-2">
            <p className="font-mono text-slate-900">100 pins, 0d = 60</p>
          </div>
          <div className="rounded bg-slate-50 p-2">
            <p className="font-mono text-slate-900">25 pins, 0d = 40</p>
          </div>
          <div className="rounded bg-slate-50 p-2">
            <p className="font-mono text-slate-900">400 pins, 6d = 83</p>
          </div>
        </div>
      </div>
    </section>
  )
}

function SystemInfoSection({ info }: { info: SystemInfo }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">🖥️ System Info</h2>
      <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
        <InfoRow label="Ruby Version" value={info.rubyVersion} />
        <InfoRow label="Rails Version" value={info.railsVersion} />
        <InfoRow label="Environment" value={info.environment} />
        <InfoRow label="Database" value={info.database} />
        <InfoRow label="Cache Store" value={info.cacheStore} />
        <InfoRow label="Time Zone" value={info.timeZone} />
      </dl>
    </section>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between rounded-lg bg-slate-50 p-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-mono text-slate-900">{value}</dd>
    </div>
  )
}
